import { ref, computed, watch, type WatchSource, type WatchStopHandle } from 'vue'
import { defineStore } from 'pinia'
import type {
	StandardMethod,
	EditMethodMetaDataRequest,
	MultiMethodRuleOverride,
	StandardRuleMetaData,
	StandardMethodMetaData,
	AddAuditCommentRequest,
} from '@/models/api-generated'

import i18n from '@/i18n'
import { isCommentRequired } from '@/services/utils'
import { removeNavLock, setNavLock } from '@/services/navlock'
import type { CustomValueMap } from '@/services/custom'
import http from '@/services/http'
import auditStore from '@/stores/audit'
import status from '@/locales/et/status'
import toastStore from '@/stores/toast'
import { useI18n } from 'vue-i18n'

export interface MethodEditModel {
	visible: boolean
	method?: StandardMethod | null
	selected: StandardMethodMetaData[]
	override?: MultiMethodRuleOverride
	massMethodCustomValues: CustomValueMap
	massMetaCustomValues: CustomValueMap
}

export default defineStore('method-editor', () => {
	const isEditMode = ref(false)
	const canSave = computed(() => {
		if (!changeCount.value) return false

		for (const [_, rule] of standardRules) {
			if (!rule.parent_id && isCommentRequired(rule.status, rule.comment)) {
				return false
			}
		}
		return true
	})
	const massEdit = ref<MethodEditModel>({
		visible: false,
		method: null,
		selected: [],
		massMetaCustomValues: [],
		massMethodCustomValues: [],
	})

	const changeCount = ref(0)

	const standardMethods = new Map<number, StandardMethodMetaData>()
	const standardRules = new Map<number, StandardRuleMetaData>()
	const methodCustomValues = ref<CustomValueMap>({})
	const metaCustomValues = ref<CustomValueMap>({})
	const audit = auditStore()
	const toaster = toastStore()
	const { t } = useI18n()
	const fetchCallback = ref<() => void | (() => Promise<void>)>()

	const watches: WatchStopHandle[] = []

	const refresh = async () => {
		watches.forEach((x) => x())
		watches.splice(0, watches.length)

		standardMethods.clear()
		standardRules.clear()
		methodCustomValues.value = {}
		metaCustomValues.value = {}
		massEdit.value.selected.splice(0, massEdit.value.selected.length)
		changeCount.value = 0

		if (fetchCallback.value) await fetchCallback.value()
	}

	watch(isEditMode, async (newMode, oldMode) => {
		if (oldMode && !newMode && changeCount.value) {
			removeNavLock('method-editor')
			const cb = window.confirm(i18n.global.t('misc.loseUnsavedChanges'))
			if (cb) {
				await refresh()
			} else {
				isEditMode.value = true
				return
			}
		}
	})

	watch(changeCount, (changes) => {
		setNavLock('method-editor', changes > 0)
	})

	function count() {
		const metas = new Set<number>()
		const methods = new Set<number>()
		for (const m of standardMethods.values()) metas.add(m.id)
		for (const r of standardRules.values()) metas.add(r.method_meta_data_id ?? 0)
		for (const c of Object.values(methodCustomValues.value)) Object.keys(c).forEach((x) => methods.add(parseInt(x)))
		for (const c of Object.values(metaCustomValues.value)) Object.keys(c).forEach((x) => metas.add(parseInt(x)))
		changeCount.value = metas.size + methods.size
	}

	function changeMethod(method: StandardMethodMetaData) {
		standardMethods.set(method.id, method)
		count()
	}

	function changeRule(rule: StandardRuleMetaData) {
		standardRules.set(rule.id, rule as any)
		count()
	}

	function changeRules(method: StandardMethodMetaData) {
		for (let i = 0; i < method.rules.length; i++) {
			changeRule(method.rules[i])
			for (let j = 0; j < method.rules[i].sub_rules.length; j++) {
				changeRule(method.rules[i].sub_rules[j])
			}
		}
	}

	async function addMethodToAudit(metadataId: number) {
		if (!metadataId) return
		if (!audit.active.length) await audit.fetch()
		const request: AddAuditCommentRequest = {
			value: '',
			auditID: 0,
			methodMetadataId: metadataId,
			kind: 'inAudit',
		}
		const { status } = await http.post('api/audit/addToAudit', [request])
		if (status === 200) {
			toaster.showSuccess(t('misc.successfullyAdded'))
			await fetchCallback.value?.()
		}
	}

	async function addSelectedToAudit() {
		if (!massEdit.value.selected.length) return
		if (!audit.active.length) await audit.fetch()

		const requests: AddAuditCommentRequest[] = massEdit.value.selected
			.map((method) => {
				return {
					value: '',
					auditID: 0,
					methodMetadataId: method.id ?? 0,
					kind: 'inAudit',
				} satisfies AddAuditCommentRequest
			})
			.filter((req): req is AddAuditCommentRequest => Boolean(req))

		if (!requests.length) return
		const { status } = await http.post('api/audit/addToAudit', requests)

		if (status === 200) {
			toaster.showSuccess(t('misc.successfullyAdded'))
			await fetchCallback.value?.()
		}
	}

	async function removeSelectedFromAudit() {
		if (!massEdit.value.selected.length) return
		if (!audit.active.length) await audit.fetch()

		const idsToRemove: number[] = massEdit.value.selected.map((method) => method.id)

		const { status } = await http.post('api/audit/removeFromAudit', idsToRemove)

		if (status === 200) {
			toaster.showSuccess(t('misc.successfullyRemoved'))
			await fetchCallback.value?.()
		}
	}

	function watchMethod(method: StandardMethodMetaData) {
		watches.push(
			watch(
				() => method.price,
				() => changeMethod(method),
			),
		)

		watches.push(
			watch(
				() => method.priority,
				() => changeMethod(method),
			),
		)

		watches.push(
			watch(
				() => method.status,
				() => changeMethod(method),
			),
		)

		watches.push(
			watch(
				() => method.dueDate,
				() => changeMethod(method),
			),
		)

		watches.push(
			watch(
				() => method.responsible,
				() => changeMethod(method),
			),
		)

		watches.push(
			watch(
				() => method.serviceProvider,
				() => changeMethod(method),
			),
		)

		watches.push(
			watch(
				() => method.comment,
				() => changeMethod(method),
			),
		)

		watches.push(
			watch(
				() => method.updatedAt,
				() => changeMethod(method),
			),
		)

		watches.push(
			watch(
				() => method.urlReferences,
				() => changeMethod(method),
				{ deep: true },
			),
		)

		watches.push(
			watch(
				() => method.documentReferences,
				() => changeMethod(method),
				{ deep: true },
			),
		)

		watches.push(
			watch(
				() => method.rules,
				() => changeRules(method),
				{ deep: true },
			),
		)
	}

	function watchCustomMethod(definitionId: number, methodId: number, src: WatchSource<string | undefined>) {
		watches.push(
			watch(src, (value) => {
				if (!methodCustomValues.value[definitionId]) methodCustomValues.value[definitionId] = {}
				methodCustomValues.value[definitionId][methodId] = value as any
				count()
			}),
		)
	}

	function watchCustomMeta(definitionId: number, metaId: number, src: WatchSource<string | undefined>) {
		watches.push(
			watch(src, (value) => {
				if (!metaCustomValues.value[definitionId]) metaCustomValues.value[definitionId] = {}
				metaCustomValues.value[definitionId][metaId] = value as any
				count()
			}),
		)
	}

	const canChangeSubRuleStatus = ref(true)

	function blockSubRuleStatusChanges() {
		canChangeSubRuleStatus.value = false
		setTimeout(() => (canChangeSubRuleStatus.value = true))
	}

	function getChanges(): EditMethodMetaDataRequest {
		return {
			standardMethods: Array.from(standardMethods.values()),
			standardRules: Array.from(standardRules.values()),
			methodCustomValues: methodCustomValues.value,
			metaCustomValues: metaCustomValues.value,
		}
	}

	return {
		canChangeSubRuleStatus,
		blockSubRuleStatusChanges,
		isEditMode,
		massEdit,
		watchMethod,
		watchCustomMethod,
		watchCustomMeta,
		getChanges,
		changeCount,
		canSave,
		refresh,
		fetchCallback,
		addSelectedToAudit,
		addMethodToAudit,
		removeSelectedFromAudit,
	}
})
